/**
 * Entrada al sistema. Dos caminos según quién esté frente al aparato.
 *
 * Si la tablet ya está dada de alta, lo primero que se ve es el teclado de
 * PIN, porque es el caso de todos los días: alguien entra a su turno. El
 * acceso con usuario y contraseña queda a un toque, para el dueño.
 */
import { useState } from 'react';
import { api, ErrorApi } from '../lib/api';
import { sesion, type Usuario } from '../lib/sesion';
import { LogoErimar } from '../componentes/LogoErimar';

interface Respuesta {
  token: string;
  usuario: Usuario;
}

export function Login() {
  const tieneDispositivo = !!sesion.tokenDispositivo;
  const [modo, setModo] = useState<'pin' | 'password'>(tieneDispositivo ? 'pin' : 'password');
  const [pin, setPin] = useState('');
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  async function entrar(fn: () => Promise<Respuesta>) {
    setCargando(true);
    setError(null);
    try {
      const r = await fn();
      sesion.abrir(r.token, r.usuario);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo entrar.');
      setPin('');
    } finally {
      setCargando(false);
    }
  }

  function teclear(digito: string) {
    if (cargando || pin.length >= 6) return;
    const nuevo = pin + digito;
    setPin(nuevo);
    setError(null);
    // Cuatro dígitos es el largo normal: entramos solos para que nadie tenga
    // que buscar el botón de aceptar con las manos ocupadas.
    if (nuevo.length === 4) {
      void entrar(() =>
        api<Respuesta>('/auth/pin', {
          method: 'POST',
          body: { dispositivo: sesion.tokenDispositivo, pin: nuevo },
        })
      );
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cal px-6">
      <div className="w-full max-w-sm">
        <div className="mb-10">
          <LogoErimar />
        </div>

        {modo === 'pin' ? (
          <>
            <div className="mb-8 flex justify-center gap-3" aria-live="polite">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={`h-4 w-4 rounded-full transition ${
                    i < pin.length ? 'bg-nopal' : 'bg-salvia/25'
                  }`}
                />
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} onClick={() => teclear(d)} className="btn-neutro h-20 text-2xl">
                  {d}
                </button>
              ))}
              <button
                onClick={() => setModo('password')}
                className="btn-neutro h-20 text-xs uppercase tracking-wider text-salvia"
              >
                Dueño
              </button>
              <button onClick={() => teclear('0')} className="btn-neutro h-20 text-2xl">
                0
              </button>
              <button
                onClick={() => setPin(pin.slice(0, -1))}
                className="btn-neutro h-20 text-xl"
                aria-label="Borrar"
              >
                ←
              </button>
            </div>
          </>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void entrar(() =>
                api<Respuesta>('/auth/login', { method: 'POST', body: { usuario, password } })
              );
            }}
          >
            <input
              className="campo"
              placeholder="Usuario"
              autoCapitalize="none"
              autoComplete="username"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
            />
            <input
              className="campo"
              type="password"
              placeholder="Contraseña"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button className="btn-principal" disabled={cargando}>
              {cargando ? 'Entrando…' : 'Entrar'}
            </button>
            {tieneDispositivo && (
              <button
                type="button"
                onClick={() => setModo('pin')}
                className="text-sm text-salvia underline"
              >
                Entrar con PIN
              </button>
            )}
          </form>
        )}

        {error && (
          <p
            role="alert"
            className="mt-6 rounded-lg bg-barro-claro px-4 py-3 text-center text-sm text-barro-oscuro"
          >
            {error}
          </p>
        )}

        {!tieneDispositivo && (
          <p className="mt-8 text-center text-xs leading-relaxed text-salvia">
            Este aparato todavía no está dado de alta. Entra como dueño y
            regístralo para que el personal pueda usar su PIN.
          </p>
        )}
      </div>
    </div>
  );
}
